import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonHttpService } from '../../services/common-http.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ExportResponse } from '../../../views/report-details/interfaces/report-details.interface';
import { CommonMethods, Severity } from '../../helpers/common-methods';
import {
  isExportRestrictedForLargeDataset,
  LARGE_DATASET_CSV_EXPORT_MESSAGE,
  LARGE_DATASET_CSV_EXPORT_TITLE,
} from '../../helpers/export-restrictions.utils';

@Component({
  selector: 'bi-drilldown-report',
  standalone: false,
  templateUrl: './drilldown-report.component.html',
  styleUrl: './drilldown-report.component.scss'
})
export class DrilldownReportComponent implements OnInit, OnChanges {
  @Input() drilldownReportInfo: any;
  @Input() drilldownColumnInfo: any;
  @Input() drilldownReportPostData: any;

  // Lazy load table variables
  rows: any[] = [];
  first: number = 0;
  totalRecords: number = 0;
  loading: boolean = false;
  private isInitialized: boolean = false;
  private dataLoadTriggered: boolean = false;

  /** Cache SafeHtml so CD (e.g. mousemove while selecting) does not reassign innerHTML and clear selection. */
  private safeHtmlCache = new Map<string, SafeHtml>();

  // Export functionality
  exportMenuItems: any[] = [];

  // Job description "Click Here" columns (same as report-details)
  jobDescriptionColumn: number[] = [1114, 975, 2156, 2191];
  showJobDescriptionPopup: boolean = false;
  currentJobTitle: string = '';
  currentJobDescription: string = '';
  jobCode: string = '';
  notesCreatedAt: string = '';
  notesDescription: string = '';
  notesCreatedBy: string = '';

  constructor(
    private _commonHttpService: CommonHttpService,
    private _commonMethods: CommonMethods,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    this.initializeExportMenu();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Reset initialization if drilldown is closed
    if (changes['drilldownReportInfo'] &&
      changes['drilldownReportInfo'].currentValue &&
      !changes['drilldownReportInfo'].currentValue.visible) {
      this.isInitialized = false;
      this.dataLoadTriggered = false;
      this.onDialogClose();
      return;
    }

    if (!this.dataLoadTriggered) {
      this.checkAndLoadInitialData();
    }
  }

  private checkAndLoadInitialData() {


    if (this.drilldownReportInfo &&
      this.drilldownColumnInfo &&
      this.drilldownReportPostData &&
      !this.isInitialized &&
      !this.dataLoadTriggered) {

      this.isInitialized = true;
      this.dataLoadTriggered = true;
      // Trigger initial data load
      this.loadInitialData();
    } else {
    }
  }

  private loadInitialData() {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.drilldownReportPostData.first = this.first;
    this.drilldownReportPostData.rows = 20;
    this._commonHttpService.getDrilldownReportData(this.drilldownReportPostData).subscribe({
      next: (data: any) => {
        if (data?.status === 0 || data?.success === 0) {
          this.loading = false;
          this.isInitialized = false;
          this.dataLoadTriggered = false;
          this._commonMethods.showToastMessage(
            data?.message || 'Failed to load drilldown report',
            'Drilldown Error',
            Severity.error
          );
          return;
        }
        this.clearSafeHtmlCache();
        this.rows = data.aaData || [];
        this.totalRecords = data.iTotalRecords || 0;
        this.first = 0;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.isInitialized = false;
        this.dataLoadTriggered = false;
        this._commonMethods.showToastMessage(
          error?.error?.message || 'Failed to load drilldown report',
          'Drilldown Error',
          Severity.error
        );
      }
    });
  }

  loadData(event: any) {
    if (this.loading) {
      return;
    }

    this.loading = true;

    // Extract pagination parameters
    this.drilldownReportPostData.first = event.first;
    this.drilldownReportPostData.rows = event.rows;

    this._commonHttpService.getDrilldownReportData(this.drilldownReportPostData).subscribe({
      next: (data: any) => {
        this.clearSafeHtmlCache();
        this.rows = data.aaData;
        this.totalRecords = data.iTotalRecords;
        this.first = event.first;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this._commonMethods.showToastMessage(
          error?.error?.message || 'Failed to load drilldown report',
          'Drilldown Error',
          Severity.error
        );
      }
    });
  }

  getUnifiedTableColumns() {
    if (!this.drilldownColumnInfo || !Array.isArray(this.drilldownColumnInfo)) {
      return [];
    }
    const columns = this.drilldownColumnInfo.map((column: any) => column.columnName);
    return columns;
  }

  isColumnInfoAvailable(): boolean {
    return this.drilldownColumnInfo && Array.isArray(this.drilldownColumnInfo);
  }

  onDialogClose() {
    this.isInitialized = false;
    this.dataLoadTriggered = false;
    this.rows = [];
    this.totalRecords = 0;
    this.first = 0;
    this.loading = false;
    this.clearSafeHtmlCache();
  }

  /**
   * Renders HTML cell content without breaking text selection/copy
   * (same SafeHtml cache approach as the main report table).
   */
  getSafeHtml(value: any): SafeHtml {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const html = String(value);
    const cached = this.safeHtmlCache.get(html);
    if (cached) {
      return cached;
    }
    const safe = this.sanitizer.bypassSecurityTrustHtml(html);
    this.safeHtmlCache.set(html, safe);
    return safe;
  }

  private clearSafeHtmlCache(): void {
    this.safeHtmlCache.clear();
  }

  getColumnInfo(columnName: any): any {
    if (!this.drilldownColumnInfo || !Array.isArray(this.drilldownColumnInfo)) {
      return null;
    }
    return this.drilldownColumnInfo.find((column: any) => column.columnName === columnName);
  }

  private isAmountField(columnInfo: any): boolean {
    return columnInfo?.isAmountField === 1;
  }

  private formatNumberWithCommas(value: number, decimalPlaces?: number): string {
    const minDecimals = decimalPlaces ?? (Number.isInteger(value) ? 0 : 2);
    const maxDecimals = decimalPlaces ?? (Number.isInteger(value) ? 0 : 2);
    return Number(value).toLocaleString('en-US', {
      minimumFractionDigits: minDecimals,
      maximumFractionDigits: maxDecimals
    });
  }

  /**
   * Formats a value based on column properties
   * @param value - The value to format
   * @param columnName - The column name to get column info
   * @returns Formatted value as string
   */
  formatValue(value: any, columnName: any): string {
    const columnInfo = this.getColumnInfo(columnName);

    if (value === null || value === undefined || value === '') {
      if (columnInfo && this.isAmountField(columnInfo)) {
        return '0.00';
      }
      return '';
    }
    if (!columnInfo) {
      return String(value);
    }

    if (columnInfo.searchType == 2) {
      return String(value);
    }

    if (columnInfo.colDataType != 1) {
      return String(value);
    }

    const normalized = String(value).trim().replace(/,/g, '');
    const numValue = /^-?\d+(\.\d+)?$/.test(normalized) ? parseFloat(normalized) : NaN;
    if (isNaN(numValue)) {
      return String(value);
    }

    if (this.isAmountField(columnInfo)) {
      return this.formatNumberWithCommas(numValue, 2);
    }

    return this.formatNumberWithCommas(numValue);
  }

  formatCurrency(value: any, rowData: any): string {
    const currencyCode = rowData[rowData.length - 1]?.col__org_1680 || '';
    if (!Number(value)) {
      return '';
    }

    const formattedValue = this.formatNumberWithCommas(Number(value));
  
    //const symbol = currencySymbols[currencyCode] || currencyCode;

    return  formattedValue;
  }

  getDisplayValue(value: any, columnName: any, rowData: any): string {
    return this.formatValue(value, columnName);
  }

  isJobDescriptionColumn(columnName: any): boolean {
    const columnId = Number(this.getColumnInfo(columnName)?.id);
    return this.jobDescriptionColumn.includes(columnId);
  }

  getJobDescription(rowData: any, columnName: any) {
    const columnId = Number(this.getColumnInfo(columnName)?.id);
    let primaryKey: any;

    if (columnId === 2191) {
      const columnIndex = this.getUnifiedTableColumns().findIndex((col: any) => col == columnName);
      primaryKey = columnIndex >= 0 ? rowData[columnIndex] : undefined;
    } else {
      primaryKey = rowData[rowData.length - 1]?.col__org_0;
    }

    const dataToSend = {
      primaryKey,
      column: columnId,
    };

    this._commonHttpService.getJobDescription(dataToSend).subscribe({
      next: (res: any) => {
        if (res.status == 1) {
          this.currentJobTitle = res.data?.jobTitle || res.data?.title || '';
          this.currentJobDescription = res.data?.jobDescription || res.data?.description || '';
          this.jobCode = res.data?.jobCode;
          this.notesCreatedAt = res.data?.createdAt || '';
          this.notesDescription = res.data?.noteDescription ? res.data?.noteDescription : '';
          this.notesCreatedBy = res.data?.CreatedBy || '';
          this.showJobDescriptionPopup = true;
        } else {
          this.currentJobTitle = res.data?.jobTitle;
          this.currentJobDescription = res.message || res.data?.requistion_description || '';
          this.showJobDescriptionPopup = true;
        }
      },
      error: () => {
        this.currentJobTitle = 'Error';
        this.currentJobDescription = 'Failed to load job description. Please try again.';
        this.showJobDescriptionPopup = true;
      }
    });
  }

  onJobDescriptionPopupHide() {
    this.showJobDescriptionPopup = false;
    this.currentJobTitle = '';
    this.currentJobDescription = '';
    this.jobCode = '';
    this.notesCreatedBy = '';
    this.notesCreatedAt = '';
    this.notesDescription = '';
  }

  /**
   * Initialize export menu items
   */
  private initializeExportMenu() {
    this.exportMenuItems = [
      { label: 'Export to Excel', icon: 'pi pi-file-excel', type: 'excel', image:'export-excel-icon.svg' }, 
      { label: 'Export to CSV', icon: 'pi pi-file-excel', type: 'csv', image:'export-csv-icon.svg' }
    ];
  }

  /**
   * Export drilldown report data
   * @param exportType - Type of export (excel, csv)
   * @author Prachethan | 12-09-2025
   */
  exportReport(exportType: string) {

    if (!this.rows || this.rows.length === 0) {
      return;
    }

    if (isExportRestrictedForLargeDataset(exportType, this.totalRecords)) {
      this._commonMethods.showToastMessage(
        LARGE_DATASET_CSV_EXPORT_MESSAGE,
        LARGE_DATASET_CSV_EXPORT_TITLE,
        Severity.warn
      );
      return;
    }

    if (this.loading) {
      return;
    }

    this.loading = true;

    const reportFormData = {
      ...this.drilldownReportPostData,
      reportName: this.drilldownReportPostData.drilldownReportInfo?.reportName,
      reportDesc: null,
      drilldownReportInfo: {
        ...this.drilldownReportPostData.drilldownReportInfo,
        exportType: exportType
      }
    };

    this._commonHttpService.getDrilldownReportData(reportFormData).subscribe({
      next: (data: ExportResponse) => {
        this.loading = false;
        if (data.success === 1) {
          this._commonMethods.showToastMessage(data.message || 'Report exported successfully', 'Report Exported', Severity.success);
          if (data.reportUrl && data.reportUrl.trim() !== '') {
            window.open(data.reportUrl, '_blank');
          }
        } else {
          this._commonMethods.showToastMessage(data.message || 'Report Export Failed', 'Report Export Failed', Severity.error);
        }
      },
      error: (error) => {
        this.loading = false;
        this._commonMethods.showToastMessage(
          error?.error?.message || 'Failed to export drilldown report',
          'Report Export Failed',
          Severity.error
        );
      }
    });
  }


}
